import { DateTime, Interval } from 'luxon'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useEngagements } from '../../../api/apiQueries'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import FilterListIcon from '../../../assets/images/icons/filter-list-filled.svg?react'
import DeleteIcon from '../../../assets/images/icons/delete.svg?react'
import { GetEngagementsParams, PagedEngagements } from '../../../api/models'
import { formatIsoTimestamp } from '../../../utils/dateUtils'
import { getOutdoorEngagementResolutionCategoryDisplayName } from '../../../constants/OutdoorEngagementResolutionCategoryDisplayNames'
import { useLocationId } from '../../../hooks/useLocationId'
import { Controller, useForm } from 'react-hook-form'
import { useQueryParams } from '../../../hooks/useQueryParams'
import { DatePicker } from '@mui/x-date-pickers'
import { FilterChips } from './FilterChips'
import { TokenPaginator } from '../../../components/TokenPaginator/TokenPaginator'
import { atom, useSetAtom } from 'jotai'

const initialPageTokens: string[] = []
const pageTokensAtom = atom<string[]>(initialPageTokens)

type QueryParams = Omit<GetEngagementsParams, 'interval'> & { startTime?: string; endTime?: string }
type FormData = Omit<GetEngagementsParams, 'interval'> & { startTime?: DateTime; endTime?: DateTime }
const blankFormData: FormData = { limit: 50 }
const emptyResults: PagedEngagements = { items: [] }

const formDataLabels: Partial<Record<keyof FormData, string>> = {
  locationId: 'SID',
  specialistId: 'Specialist ID',
  startTime: 'Start Date',
  endTime: 'End Date'
}

export const EngagementSearch: FC = () => {
  const navigate = useNavigate()
  const { locationId, setLocationId } = useLocationId()
  const setPageTokens = useSetAtom(pageTokensAtom)

  const filterButtonRef = useRef(null)

  const [showFilter, setShowFilter] = useState(false)

  // the queryParams have all the filter params except for the locationId because it is a path param
  const [queryParams, setQueryParams] = useQueryParams<QueryParams>()

  // the filterParam have all the params in the shape that the form needs
  const filterParams: FormData = useMemo(() => {
    const { startTime, endTime, ...rest } = queryParams
    return {
      ...blankFormData,
      locationId,
      startTime: startTime ? DateTime.fromISO(startTime) : undefined,
      endTime: endTime ? DateTime.fromISO(endTime) : undefined,
      ...rest
    }
  }, [locationId, queryParams])

  // this sets the filter params, separating out the locationId to set it on the path
  const saveParamsToUrl = useCallback(
    ({ locationId: newLocationId, startTime, endTime, ...queryParams }: FormData) => {
      setQueryParams({
        startTime: startTime?.toISO() ?? undefined,
        endTime: endTime?.toISO() ?? undefined,
        ...queryParams
      })
      if ((locationId ?? undefined) !== (newLocationId ?? undefined)) {
        setLocationId(newLocationId)
      }
    },
    [locationId, setLocationId, setQueryParams]
  )

  const clearPageTokens = useCallback(() => {
    setPageTokens(initialPageTokens)
  }, [setPageTokens])

  const clearFilter = useCallback(() => {
    clearPageTokens()
    saveParamsToUrl(blankFormData)
    setShowFilter(false)
  }, [saveParamsToUrl, clearPageTokens])

  const [currentResults, setCurrentResults] = useState<PagedEngagements>(emptyResults)
  const useEngagementsParams: GetEngagementsParams = fromFilterParamsToGetEngagementsParams(filterParams)
  const engagementsQuery = useEngagements(useEngagementsParams)
  const pagedEngagements: PagedEngagements = engagementsQuery.data ?? emptyResults

  useEffect(() => {
    if (pagedEngagements !== emptyResults && pagedEngagements !== currentResults) {
      setCurrentResults(pagedEngagements)
    }
  }, [currentResults, pagedEngagements])

  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
    getValues,
    setValue
  } = useForm<FormData>({ values: filterParams, mode: 'onChange' })
  const onSubmit = handleSubmit((filterForm: GetEngagementsParams) => {
    clearPageTokens()
    saveParamsToUrl(filterForm)
  })

  const endTime = watch('endTime')
  const startTime = watch('startTime')

  const handleFilterClick = useCallback(() => {
    setShowFilter(true)
  }, [])

  const handleFilterClose = useCallback(async () => {
    await onSubmit()
    setShowFilter(false)
  }, [onSubmit])

  const onPaginatorClick = useCallback(
    (nextToken?: string) => {
      if (nextToken) {
        saveParamsToUrl({ ...getValues(), nextToken: nextToken })
      } else {
        const { nextToken, ...rest } = getValues()
        saveParamsToUrl(rest)
      }
    },
    [getValues, saveParamsToUrl]
  )

  return (
    <Paper data-testid="engagements" sx={{ backgroundColor: 'var(--bg-surface)' }}>
      <Stack spacing={'var(--spacing-2xs)'} sx={{ p: 'var(--spacing-s)' }}>
        <Stack direction={'row'} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant={'h2'} data-testid="engagements-table-heading">
            Engagements
          </Typography>
          <Button ref={filterButtonRef} variant="outlined" onClick={handleFilterClick} disableFocusRipple data-testid="filter-button">
            <FilterListIcon />
          </Button>
          <Popover
            open={showFilter}
            onClose={handleFilterClose}
            anchorEl={filterButtonRef.current}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'right'
            }}
            // Add custom styles to the Popover's internal Paper component
            slotProps={{
              paper: {
                style: {
                  // This pushes the popover an extra 20px left, creating the gap
                  transform: 'translateX(var(--spacing-s))'
                }
              }
            }}
          >
            <form onSubmit={onSubmit} data-testid="engagement-filters-form">
              <Box sx={{ padding: 'var(--spacing-l)' }}>
                <Stack direction={'row'} spacing={'var(--spacing-m)'}>
                  <Button variant="text" onClick={clearFilter} sx={{ padding: 0, minWidth: 0 }}>
                    <DeleteIcon />
                  </Button>
                  <Controller
                    name="startTime"
                    control={control}
                    rules={{
                      validate: {
                        dateRange: value =>
                          validateDateRange(value, endTime) || `${formDataLabels['startTime']} must be before ${formDataLabels['endTime']}`
                      }
                    }}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <DatePicker
                        {...rest}
                        value={value ?? null} // can't send undefined or else it will make it an uncontrolled component
                        label={formDataLabels['startTime']}
                        slotProps={{
                          textField: {
                            inputProps: { 'data-testid': 'start-time' },
                            error: !!errors.startTime,
                            helperText: errors.startTime ? errors.startTime.message : undefined
                          }
                        }}
                        onChange={e => {
                          onChange(e)
                          onSubmit()
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="endTime"
                    control={control}
                    rules={{
                      validate: {
                        dateRange: value =>
                          validateDateRange(startTime, value) || `${formDataLabels['endTime']} must be after ${formDataLabels['startTime']}`
                      }
                    }}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <DatePicker
                        {...rest}
                        value={value ?? null} // can't send undefined or else it will make it an uncontrolled component
                        label={formDataLabels['endTime']}
                        slotProps={{
                          textField: {
                            inputProps: { 'data-testid': 'end-time' },
                            error: !!errors.endTime,
                            helperText: errors.endTime ? errors.endTime.message : undefined
                          }
                        }}
                        onChange={e => {
                          onChange(e)
                          onSubmit()
                        }}
                      />
                    )}
                  />
                  <TextField
                    {...register('locationId')}
                    slotProps={{
                      htmlInput: { ['data-testid']: 'locationId' }
                    }}
                    label={formDataLabels['locationId']}
                    placeholder={`Enter ${formDataLabels['locationId']}`}
                  />
                  <TextField
                    {...register('specialistId')}
                    slotProps={{
                      htmlInput: { ['data-testid']: 'specialistId' }
                    }}
                    label={formDataLabels['specialistId']}
                    placeholder={'123456'}
                  />
                  {/*this is here so that the form can be submitted by hitting enter, but it is hidden because the design doesn't have a submit button*/}
                  <Button type="submit" sx={{ display: 'none' }} data-testid={'submit-button'}>
                    Filter
                  </Button>
                </Stack>
              </Box>
            </form>
          </Popover>
        </Stack>
        <FilterChips
          formDataLabels={formDataLabels}
          values={getValues()}
          onDeleteChip={formPropertyName => {
            setValue(formPropertyName, undefined)
            onSubmit()
          }}
        />
      </Stack>
      <TableContainer sx={{ position: 'relative' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ '&>th': { color: 'var(--text-secondary)', fontWeight: 500, fontSize: 'var(--size-2xs)' } }}>
              <TableCell>ENGAGEMENT ID</TableCell>
              <TableCell>TYPE</TableCell>
              <TableCell>DATE &amp; TIME</TableCell>
              <TableCell>CATEGORY</TableCell>
              <TableCell>SPECIALIST ID</TableCell>
              <TableCell>SID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentResults.items.map(engagement => {
              if (!engagement) {
                return null
              }
              const engagementUrl = `/locations/${engagement.locationId}/engagement-review/${engagement.id}`
              return (
                <TableRow
                  key={engagement.id}
                  sx={{
                    // TODO: --button-video is the only theme token with the exact colors provided by design via slack.
                    // there might be another theme token in the future with a better name, but for this works
                    '&:nth-of-type(odd)': { backgroundColor: 'var(--button-video)' },
                    '&:hover': { backgroundColor: 'var(--bg-cell-hover)', cursor: 'pointer' }
                  }}
                  onClick={() => navigate(engagementUrl)}
                >
                  <TableCell>
                    <Link
                      component={NavLink}
                      to={engagementUrl}
                      // stop propagation to prevent the row onClick from firing. this allows the link to be opened in a new tab.
                      onClick={e => e.stopPropagation()}
                      data-testid="ert-link"
                    >
                      {engagement.id}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Chip label={engagement.type} sx={{ '&': { backgroundColor: 'var(--shape-confirm-transparent)' } }} />
                  </TableCell>
                  <TableCell>{formatIsoTimestamp(engagement.createdTimestamp)}</TableCell>
                  <TableCell>{getOutdoorEngagementResolutionCategoryDisplayName(engagement.result?.resolution.category)}</TableCell>
                  <TableCell>{engagement.monitoringSpecialistAssignment?.monitoringSpecialist.id}</TableCell>
                  <TableCell>{engagement.locationId}</TableCell>
                </TableRow>
              )
            })}
            {!engagementsQuery.isLoading && currentResults.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align={'center'} data-testid="no-results">
                  No results found
                </TableCell>
              </TableRow>
            )}
            {engagementsQuery.isLoading && currentResults.items.length === 0 && (
              <TableRow>
                <TableCell>&nbsp;</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {engagementsQuery.isLoading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        )}
        <TokenPaginator
          pageTokensAtom={pageTokensAtom}
          currentToken={useEngagementsParams.nextToken}
          nextToken={currentResults.nextToken}
          onClick={onPaginatorClick}
        />
      </TableContainer>
    </Paper>
  )
}

function fromFilterParamsToGetEngagementsParams(filterParams: FormData): GetEngagementsParams {
  const { startTime, endTime, ...rest } = filterParams

  let interval: Interval | undefined
  if (startTime && endTime) {
    // validate interval
    interval = Interval.fromDateTimes(startTime, endTime.endOf('day'))
  } else if (startTime && !endTime) {
    // set end time to now
    interval = Interval.after(startTime, { year: 100 })
  } else if (!startTime && endTime) {
    // set end time to now
    interval = Interval.before(endTime.endOf('day'), { year: 100 })
  }

  return {
    ...rest,
    interval: interval?.toISO()
  }
}

const validateDateRange = (startDate: DateTime | null | undefined, endDate: DateTime | null | undefined) => {
  if (!startDate || !endDate) {
    return true
  }
  return startDate < endDate.endOf('day')
}
